import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  AppBar, 
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  ArrowBack,
  PersonAdd,
  People,
  Dashboard,
  QrCode
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminRegister from './AdminRegister';
import UserManagement from './UserManagement';
import QridManagement from './QridManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `admin-tab-${index}`,
    'aria-controls': `admin-tabpanel-${index}`,
  };
};

const AdminPanel: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="back"
            sx={{ mr: 2 }}
            onClick={handleBack}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Admin Panel
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Container component="main" maxWidth="lg" sx={{ mt: 4 }}>
        <Paper 
          elevation={3} 
          sx={{ 
            width: '100%',
            mb: 4
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="admin functions tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab 
                icon={<Dashboard />} 
                label="Overview" 
                {...a11yProps(0)} 
                iconPosition="start"
              />
              <Tab 
                icon={<PersonAdd />} 
                label="Create User" 
                {...a11yProps(1)} 
                iconPosition="start"
              />
              <Tab 
                icon={<People />} 
                label="Manage Users" 
                {...a11yProps(2)} 
                iconPosition="start"
              />
              <Tab
                icon={<QrCode />}
                label="QR Records"
                {...a11yProps(3)}
                iconPosition="start"
              />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Typography component="h1" variant="h4" gutterBottom>
              Administrator Dashboard
            </Typography>
            
            <Typography variant="body1" paragraph>
              Welcome to the Admin Panel. From here you can manage all aspects of your web miniprogram.
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            
            <Typography variant="body2" paragraph>
              • Use the "Create User" tab to add new users to the system
            </Typography>
            
            <Typography variant="body2" paragraph>
              • Use the "Manage Users" tab to view, edit, and delete existing users
            </Typography>
            
            <Typography variant="body2" paragraph>
              • Use the "QR Records" tab to manage QR ID records
            </Typography>
            
            <Typography variant="body2" paragraph>
              • Admins have full access to all system functions
            </Typography>
            
            <Typography variant="body2" paragraph>
              • Regular users can only access their assigned features
            </Typography>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <AdminRegister />
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <UserManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <QridManagement />
          </TabPanel>
        </Paper>
      </Container>
    </>
  );
};

export default AdminPanel; 